<template>
  <div class="signature-pad-container">
    <div class="signature-canvas-wrapper">
      <canvas
        ref="canvas"
        class="signature-canvas"
        :width="width"
        :height="height"
        @mousedown="startDrawing"
        @mousemove="draw"
        @mouseup="stopDrawing"
        @mouseout="stopDrawing"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="stopDrawing"
      />
      <div v-if="isEmpty" class="signature-placeholder">
        Sign here
      </div>
    </div>
    <div class="signature-controls">
      <BaseButton
        variant="secondary"
        size="sm"
        text="Clear"
        @click="clear"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'

interface Props {
  width?: number
  height?: number
  strokeColor?: string
  strokeWidth?: number
}

const props = withDefaults(defineProps<Props>(), {
  width: 500,
  height: 200,
  strokeColor: '#000000',
  strokeWidth: 2
})

const emit = defineEmits<{
  'update:signature': [dataUrl: string]
  'clear': []
}>()

const canvas = ref<HTMLCanvasElement>()
const isDrawing = ref(false)
const isEmpty = ref(true)
let context: CanvasRenderingContext2D | null = null

onMounted(() => {
  if (canvas.value) {
    context = canvas.value.getContext('2d')
    if (context) {
      context.strokeStyle = props.strokeColor
      context.lineWidth = props.strokeWidth
      context.lineCap = 'round'
      context.lineJoin = 'round'
    }
  }
})

const getMousePos = (e: MouseEvent): { x: number; y: number } => {
  const rect = canvas.value!.getBoundingClientRect()
  return {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  }
}

const getTouchPos = (e: TouchEvent): { x: number; y: number } => {
  const rect = canvas.value!.getBoundingClientRect()
  return {
    x: e.touches[0].clientX - rect.left,
    y: e.touches[0].clientY - rect.top
  }
}

const startDrawing = (e: MouseEvent) => {
  isDrawing.value = true
  isEmpty.value = false
  const pos = getMousePos(e)
  if (context) {
    context.beginPath()
    context.moveTo(pos.x, pos.y)
  }
}

const draw = (e: MouseEvent) => {
  if (!isDrawing.value || !context) return
  const pos = getMousePos(e)
  context.lineTo(pos.x, pos.y)
  context.stroke()
}

const handleTouchStart = (e: TouchEvent) => {
  e.preventDefault()
  isDrawing.value = true
  isEmpty.value = false
  const pos = getTouchPos(e)
  if (context) {
    context.beginPath()
    context.moveTo(pos.x, pos.y)
  }
}

const handleTouchMove = (e: TouchEvent) => {
  e.preventDefault()
  if (!isDrawing.value || !context) return
  const pos = getTouchPos(e)
  context.lineTo(pos.x, pos.y)
  context.stroke()
}

const stopDrawing = () => {
  if (isDrawing.value) {
    isDrawing.value = false
    emitSignature()
  }
}

const clear = () => {
  if (context && canvas.value) {
    context.clearRect(0, 0, canvas.value.width, canvas.value.height)
    isEmpty.value = true
    emit('clear')
    emit('update:signature', '')
  }
}

const emitSignature = () => {
  if (canvas.value && !isEmpty.value) {
    const dataUrl = canvas.value.toDataURL('image/png')
    emit('update:signature', dataUrl)
  }
}

const toDataURL = (): string => {
  return canvas.value?.toDataURL('image/png') || ''
}

const toBlob = (): Promise<Blob | null> => {
  return new Promise((resolve) => {
    canvas.value?.toBlob((blob) => {
      resolve(blob)
    }, 'image/png')
  })
}

defineExpose({
  clear,
  toDataURL,
  toBlob,
  isEmpty
})
</script>

<style scoped>
.signature-pad-container {
  width: 100%;
}

.signature-canvas-wrapper {
  position: relative;
  border: 2px solid #e5e7eb;
  border-radius: 0.375rem;
  background-color: white;
  overflow: hidden;
}

.signature-canvas {
  display: block;
  touch-action: none;
  cursor: crosshair;
}

.signature-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #9ca3af;
  font-size: 1.125rem;
  pointer-events: none;
  user-select: none;
}

.signature-controls {
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-end;
}
</style> 